import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Table2, Search } from 'lucide-react';

/**
 * Add Entity dialog — extracted from SemanticModelsPage.
 * Lists tables from the linked source that are not already in the model.
 */
export function AddEntityDialog({
  open, onOpenChange,
  sourceTables,
  sourceTablesLoading,
  entities,
  model,
  onPickTable,
  saving,
}) {
  const [query, setQuery] = useState('');
  const [pickedTable, setPickedTable] = useState(null);
  useEffect(() => { if (!open) { setQuery(''); setPickedTable(null); } }, [open]);
  useEffect(() => { if (!saving) setPickedTable(null); }, [saving]);

  const eligible = sourceTables.filter(t => !entities.some(e => e.table_name === t.table_name));
  const q = query.trim().toLowerCase();
  const filtered = q ? eligible.filter(t => (t.table_name || '').toLowerCase().includes(q)) : eligible;

  const handlePick = (t) => { setPickedTable(t.table_name); onPickTable(t); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Add Entity</DialogTitle></DialogHeader>
        <div className="mt-2">
          {sourceTablesLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 text-primary animate-spin mx-auto" /></div>
          ) : sourceTables.length > 0 ? (
            <>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/80 pointer-events-none" />
                <input
                  autoFocus
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search tables..."
                  data-testid="add-entity-search"
                  className="w-full pl-9 pr-3 py-2 rounded-xl bg-muted/30 border border-border/40 text-xs text-foreground placeholder:text-muted-foreground/80 focus:outline-none focus:border-primary/40"
                />
              </div>
              {filtered.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-auto">
                  {filtered.map(t => {
                    const isLoading = saving && pickedTable === t.table_name;
                    return (
                    <button
                      key={t.table_name}
                      onClick={() => handlePick(t)}
                      disabled={saving}
                      data-testid={`add-entity-option-${t.table_name}`}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border border-border/30 text-left hover:border-primary/30 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" /> : <Table2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate">{t.table_name}</p>
                        <p className="text-[10px] text-muted-foreground">{isLoading ? 'Loading fields…' : `${(t.columns || []).length} columns`}</p>
                      </div>
                    </button>
                    );
                  })}
                </div>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">No tables match "{query}"</p>
              )}
            </>
          ) : (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {model && model.source_id ? 'No tables found' : 'No linked data source'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
